import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import amcService from '../services/amcService';

// Async thunks
export const fetchAMCs = createAsyncThunk(
  'amc/fetchAMCs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await amcService.getAMCs(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch AMCs');
    }
  }
);

export const createAMC = createAsyncThunk(
  'amc/createAMC',
  async (amcData, { rejectWithValue }) => {
    try {
      const response = await amcService.createAMC(amcData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create AMC');
    }
  }
);

export const updateAMC = createAsyncThunk(
  'amc/updateAMC',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await amcService.updateAMC(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update AMC');
    }
  }
);

export const deleteAMC = createAsyncThunk(
  'amc/deleteAMC',
  async (id, { rejectWithValue }) => {
    try {
      await amcService.deleteAMC(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete AMC');
    }
  }
);

const initialState = {
  list: [],
  current: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

const amcSlice = createSlice({
  name: 'amc',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentAMC: (state, action) => {
      state.current = action.payload;
    },
    clearCurrentAMC: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch AMCs
      .addCase(fetchAMCs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAMCs.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.amcs;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAMCs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create AMC
      .addCase(createAMC.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAMC.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload.amc);
      })
      .addCase(createAMC.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update AMC
      .addCase(updateAMC.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAMC.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(amc => amc._id === action.payload.amc._id);
        if (index !== -1) {
          state.list[index] = action.payload.amc;
        }
        state.current = action.payload.amc;
      })
      .addCase(updateAMC.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete AMC
      .addCase(deleteAMC.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAMC.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(amc => amc._id !== action.payload);
      })
      .addCase(deleteAMC.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentAMC, clearCurrentAMC } = amcSlice.actions;
export default amcSlice.reducer;